import { Card } from '../ui/Card';
import { format, formatDistanceToNow } from 'date-fns';
import { projectionsService } from '@/services/projectionsService';
import { ProjectionEntity } from '@/types/projections';
import { Calendar, Clock, AlertCircle, CheckCircle2, Circle } from 'lucide-react';
import { Progress } from '../ui/Progress';

interface CompletionTimelineProps {
  entity: ProjectionEntity;
}

export function CompletionTimeline({ entity }: CompletionTimelineProps) {
  const projections = projectionsService.projectCompletionDates(entity);
  
  // Sort by projected date
  const sortedProjections = [...projections].sort((a, b) => 
    a.projectedDate.getTime() - b.projectedDate.getTime()
  );

  // Take first 10 for display
  const displayProjections = sortedProjections.slice(0, 10);

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 70) return 'text-green-500';
    if (confidence >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 70) return 'High confidence';
    if (confidence >= 40) return 'Medium confidence';
    return 'Low confidence';
  };

  const getStatusIcon = (progress: number) => {
    if (progress === 100) return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    if (progress > 0) return <Clock className="h-4 w-4 text-yellow-500" />;
    return <Circle className="h-4 w-4 text-gray-400" />;
  };

  if (displayProjections.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Projected Completion Timeline</h3>
        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
          <AlertCircle className="h-12 w-12 mb-4" />
          <p className="text-center">No incomplete {entity} to project</p>
          <p className="text-sm text-center mt-2">
            All {entity} are either completed or have insufficient data for projection
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Projected Completion Timeline</h3>
        <span className="text-sm text-muted-foreground">
          Next {displayProjections.length} items
        </span>
      </div>
      
      <div className="space-y-4">
        {displayProjections.map((projection, index) => (
          <div 
            key={projection.itemId} 
            className="flex items-start gap-4 py-3 border-b last:border-0"
          >
            {/* Timeline indicator */}
            <div className="flex flex-col items-center">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                ${index === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
              `}>
                {index + 1}
              </div>
              {index < displayProjections.length - 1 && (
                <div className="w-0.5 h-16 bg-border mt-2" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(projection.currentProgress)}
                    <p className="font-medium">{projection.itemTitle}</p>
                  </div>
                  {projection.currentProgress > 0 && (
                    <div className="mt-2">
                      <Progress value={projection.currentProgress} className="h-1.5" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {projection.currentProgress}% complete
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="text-right ml-4">
                  <div className="flex items-center gap-1 text-sm">
                    <Calendar className="h-3 w-3" />
                    <span className="font-medium">
                      {format(projection.projectedDate, 'MMM d, yyyy')}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(projection.projectedDate, { addSuffix: true })}
                  </p>
                  <p className={`text-xs mt-1 ${getConfidenceColor(projection.confidenceLevel)}`}>
                    {getConfidenceLabel(projection.confidenceLevel)}
                  </p>
                </div>
              </div>

              {/* Velocity info */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>Daily velocity: {projection.basedOnVelocity.dailyAverage.toFixed(1)}</span>
                <span>•</span>
                <span>Trend: {projection.basedOnVelocity.trend}</span>
              </div>
            </div>
          </div>
        ))}

        {sortedProjections.length > 10 && (
          <div className="pt-4 text-center">
            <p className="text-sm text-muted-foreground">
              And {sortedProjections.length - 10} more {entity}...
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}